"use client";

import { TVacancyCard } from "@shared/cms";
import { formatDate, formatRelativeTime, getSlug, imageFallback } from "@shared/utils";
import { Card, CardContent, CardFooter, CardHeader } from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";
import { Vacancy, VacancyableType } from "../types";
import { VacancyPlatformInfo } from "./vacancy-platform-info";
import { VacancySalaryInfo } from "./vacancy-salary-info";

type VacancyCardProps = {
  vacancy: Vacancy;
  meta: TVacancyCard;
  onClick?: (vacancy: Vacancy) => void;
};
export function VacancyCard({ vacancy, meta, onClick }: VacancyCardProps) {
  const handleClick = () => {
    if (onClick) {
      onClick(vacancy);
    }
  };

  return (
    <>
      <Card className="w-full h-full bg-white border border-gray-200 rounded-xl flex flex-col justify-between relative overflow-hidden shadow-none">
        <div>
          {vacancy.vacancyableType !== VacancyableType.MICRO_VACANCY && (
            <CardHeader className="p-4 flex items-start justify-between flex-row">
              <Image
                src={
                  vacancy.companyLogoUri || vacancy.employer?.employerable?.logoUri || vacancy.job?.employer?.logoUri || "/blank-white.webp"
                }
                alt={vacancy.companyName || vacancy.employer?.name || vacancy.job?.employer?.name}
                width="80"
                height="80"
                className="size-16 xl:size-20 bg-white border border-grey-200 rounded-lg object-contain"
                onError={imageFallback()}
              />
              {vacancy.publishedAt && (
                <span className="text-[10px] font-normal text-gray-600 text-right">{formatRelativeTime(vacancy.publishedAt)}</span>
              )}
            </CardHeader>
          )}
          <CardContent className="p-4">
            <div className="max-h-24 overflow-hidden flex flex-col gap-0.5">
              <h4 className="font-bold text-base overflow-hidden max-h-[48px]">
                {vacancy.vacancyableType === VacancyableType.MICRO_VACANCY ? (
                  <Link
                    href={`/lowongan-dalam-negeri/lowongan-mikro/${getSlug(vacancy.id, vacancy.title || vacancy.job?.title)}`}
                    className="hover:text-primary"
                    onClick={handleClick}
                  >
                    {vacancy.title || vacancy.job?.title}
                  </Link>
                ) : (
                  <Link
                    href={`/lowongan-dalam-negeri/lowongan/${getSlug(vacancy.id, vacancy.title)}`}
                    className="hover:text-primary"
                    onClick={handleClick}
                  >
                    {vacancy.title}
                  </Link>
                )}
              </h4>
              <p className="font-normal text-base truncate">
                {vacancy.employer?.name || vacancy.companyName || vacancy.job?.employer?.name}
              </p>
              <p className="text-xs font-normal text-gray-500 truncate mt-1">
                {vacancy.region?.name || vacancy.cityName || vacancy.job?.region?.name}
              </p>
            </div>
          </CardContent>
        </div>
        <CardFooter className="px-4 pb-4 block">
          {meta.salaryLabel && meta.salaryInfo && (
            <div className="flex flex-col justify-start items-start gap-0.5">
              <div className="text-xs text-grey-700">{meta.salaryLabel}</div>
              <VacancySalaryInfo
                show={vacancy.showSalary}
                min={vacancy.minSalaryAmount || vacancy.job?.minBudgetAmount}
                max={vacancy.maxSalaryAmount || vacancy.job?.maxBudgetAmount}
                meta={meta.salaryInfo}
                className="text-base sm:text-lg"
              />
            </div>
          )}
          {vacancy.expiresAt && (
            <div className="text-xs text-gray-700 mt-2">
              Lamar sebelum {formatDate(vacancy.expiresAt || vacancy.job?.expireAt, "dd MMM yyyy")}
            </div>
          )}
          <div className="text-xs text-primary border border-primary mt-2 rounded-full px-1.5 py-0.5 inline-flex">
            <span>Tersisa</span>&nbsp;<strong>{vacancy.availableQuota || vacancy.quota - vacancy.accepted || 0} lowongan</strong>
          </div>
          {vacancy.vacancyableType === VacancyableType.MICRO_VACANCY && vacancy.publishedAt && (
            <div className="text-[10px] font-normal text-gray-600 mt-2">{formatRelativeTime(vacancy.publishedAt)}</div>
          )}
          {meta.platformInfo && <VacancyPlatformInfo vacancy={vacancy} meta={meta.platformInfo} />}
        </CardFooter>
      </Card>
    </>
  );
}
